// Recharts writes its colours into SVG presentation attributes (stroke, fill),
// and `var(--co-ink)` is NOT resolved there — a CSS custom property only works
// where a CSS property is expected. So a chart that wants to follow the theme
// has to hand recharts a concrete colour.
//
// Reading the token off the document gives us that concrete colour AND keeps
// dark mode honest: the ivory palette's near-black ink would be invisible on
// the dark canvas, so a hard-coded hex is a chart that works in one theme only.

import { useEffect, useState } from 'react';

/**
 * Resolve CSS custom properties (`--co-ink`) to their computed values, and
 * re-resolve when the theme flips.
 *
 * Returns the token names unchanged during SSR or before the first paint, which
 * recharts renders as "no colour" for a frame rather than the wrong colour.
 */
export function useThemeColors<T extends Record<string, string>>(tokens: T): T {
  const read = (): T => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return tokens;
    const styles = getComputedStyle(document.documentElement);
    const out = {} as Record<string, string>;
    for (const [key, token] of Object.entries(tokens)) {
      const value = styles.getPropertyValue(token).trim();
      out[key] = value || token;
    }
    return out as T;
  };

  const [colors, setColors] = useState<T>(read);

  // Every chart in the app calls this hook now, so it must not hand back a new
  // object on each pass — that would defeat the useMemo in useChartAccessibility
  // and re-render every chart for nothing.
  const apply = () =>
    setColors((prev) => {
      const next = read();
      const same = Object.keys(next).every((k) => next[k] === (prev as Record<string, string>)[k]);
      return same ? prev : next;
    });

  useEffect(() => {
    apply();
    if (typeof document === 'undefined') return;
    // The appearance is a class on <html> and the theme is written there as
    // inline custom properties, so BOTH attributes have to be watched: a theme
    // swap changes `style` without touching `class`, and a chart that only
    // watched `class` would keep painting the previous theme's colours until
    // something else re-rendered it.
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-app-theme'],
    });
    return () => observer.disconnect();
    // `tokens` is a literal at every call site; keying on its values keeps the
    // effect from re-subscribing on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.values(tokens).join('|')]);

  return colors;
}
